/**
 * Hub's binding of the canonical deal-stage vocabulary.
 *
 * The stage list, the stage sets, and the predicates live in
 * `@west-tennessee-consulting/crm-scoring/deal-stages` — promoted there because the scorers reason over
 * them and the native clients and reporting need the same list. This keeps
 * `@/lib/constants/deal-stages` working as the import path ~55 Hub files use.
 *
 * Still in sync with the Postgres `deal_stage` enum (see
 * supabase/migrations/20260605000000_migrate_deal_stage_enum_v2.sql) — add a stage
 * to the enum and to the package, and everything sees it at once.
 *
 * NOTE for win metrics: a deal almost never SITS at `deal_won` — it progresses to
 * delivery and ultimately `paid`. Use `isWonDeal` / `WON_DEAL_STAGES`, never
 * `stage === "deal_won"`, which undercounts to ~0.
 */

export * from "@west-tennessee-consulting/crm-scoring/deal-stages";

import type { DealStage } from "@west-tennessee-consulting/crm-scoring/deal-stages";

/**
 * `deals.stage` as READ from a row.
 *
 * The column is NULLABLE in Postgres, so the codegen type is
 * `stage?: Maybe<Deal_Stage>` and a row can carry `null` (no stage set) or,
 * on a partially-constructed row, nothing at all. The hand-written `Deal` type
 * used to claim the column was required, which is how ~15 read sites ended up
 * calling `isWonDeal(deal.stage)` unguarded (#2633).
 */
export type MaybeDealStage = DealStage | null | undefined;

/**
 * Narrow a row's nullable stage to the enum the canonical predicates take.
 *
 * A stage-less deal belongs to NO bucket — not won, not active, not lost —
 * which is exactly what `STAGE_SET.includes(undefined)` already evaluated to at
 * runtime. Use it with the predicates rather than reimplementing them:
 * `hasDealStage(d.stage) && isWonDeal(d.stage)`.
 */
export function hasDealStage(stage: MaybeDealStage): stage is DealStage {
  return stage != null;
}
